/**
 * renderOptimizer.ts
 * 
 * 职责：
 * 1. 提供渲染优化工具，解决长时间拖拽元素后画布卡顿的问题
 * 2. 实现渲染节流和缓存机制
 * 3. 监控渲染性能并提供优化建议
 */

import { useRef, useEffect } from 'react';
import { performanceMonitor } from './performanceMonitor';

// 渲染缓存接口
interface RenderCache {
  timestamp: number;
  elementCount: number;
  viewportScale: number;
  viewportOffset: { x: number, y: number };
  selectionActive: boolean;
}

/**
 * 使用渲染优化器Hook
 * 解决长时间拖拽元素后画布卡顿问题
 */
export const useRenderOptimizer = (renderFunction: () => void, threshold: number = 33) => {
  // 跟踪上一次渲染时间
  const lastRenderTimeRef = useRef<number>(0);
  // 跟踪渲染帧ID
  const renderFrameIdRef = useRef<number | null>(null);
  // 跟踪操作类型
  const operationTypeRef = useRef<'none' | 'viewport_drag' | 'element_drag'>('none');
  // 跟踪连续操作计数
  const operationCountRef = useRef<number>(0);
  // 渲染缓存
  const renderCacheRef = useRef<RenderCache | null>(null);
  // 是否需要重置渲染状态
  const needsResetRef = useRef<boolean>(false);
  
  // 优化的渲染函数
  const optimizedRender = (timestamp: number) => {
    // 如果没有活跃的渲染帧请求，直接返回
    if (renderFrameIdRef.current === null) return;
    
    // 计算距离上次渲染的时间差
    const timeSinceLastRender = timestamp - lastRenderTimeRef.current;
    
    // 根据操作类型动态调整渲染频率
    let currentThreshold = threshold;
    
    // 元素拖拽操作后需要更低的渲染频率来避免卡顿
    if (operationTypeRef.current === 'element_drag' && operationCountRef.current > 100) {
      // 拖拽操作次数超过阈值，降低渲染频率
      currentThreshold = 50; // 约20fps，降低渲染频率
    }
    
    // 如果时间差超过阈值，执行渲染
    if (timeSinceLastRender >= currentThreshold || needsResetRef.current) {
      // 执行实际渲染
      renderFunction();
      
      // 更新上次渲染时间
      lastRenderTimeRef.current = timestamp;
      
      // 重置标志
      needsResetRef.current = false;
      
      // 记录渲染事件
      performanceMonitor.trackEvent('canvas_render');
    }
    
    // 继续请求下一帧
    renderFrameIdRef.current = requestAnimationFrame(optimizedRender);
  };
  
  // 启动渲染循环
  const startRenderLoop = () => {
    if (renderFrameIdRef.current === null) {
      renderFrameIdRef.current = requestAnimationFrame(optimizedRender);
    }
  };
  
  // 停止渲染循环
  const stopRenderLoop = () => {
    if (renderFrameIdRef.current !== null) {
      cancelAnimationFrame(renderFrameIdRef.current);
      renderFrameIdRef.current = null;
    }
  };
  
  // 设置当前操作类型
  const setOperationType = (type: 'none' | 'viewport_drag' | 'element_drag') => {
    // 如果操作类型改变，重置计数
    if (operationTypeRef.current !== type) {
      operationCountRef.current = 0;
    }
    operationTypeRef.current = type;
  };
  
  // 增加操作计数
  const incrementOperationCount = () => {
    operationCountRef.current += 1;
  };
  
  // 重置渲染状态
  const resetRenderState = () => {
    operationTypeRef.current = 'none';
    operationCountRef.current = 0;
    needsResetRef.current = true;
    
    // 强制立即渲染一次
    if (renderFrameIdRef.current === null) {
      renderFunction();
    }
  };
  
  // 清理函数
  useEffect(() => {
    return () => {
      stopRenderLoop();
    };
  }, []);
  
  return {
    startRenderLoop,
    stopRenderLoop,
    setOperationType,
    incrementOperationCount,
    resetRenderState
  };
};

/**
 * 使用渲染监控Hook
 * 监控渲染性能并在必要时提供优化
 */
export const useRenderMonitor = () => {
  const frameTimeRef = useRef<number>(0);
  const lagCountRef = useRef<number>(0);
  const totalFramesRef = useRef<number>(0);
  
  useEffect(() => {
    let rafId: number;
    
    const monitorFrameRate = (timestamp: number) => {
      if (frameTimeRef.current) {
        const delta = timestamp - frameTimeRef.current;
        totalFramesRef.current += 1;
        
        // 检测是否卡顿（帧间隔超过33ms，约30fps）
        if (delta > 33) {
          lagCountRef.current += 1;
          
          // 记录卡顿事件
          performanceMonitor.trackEvent('render_lag_detected');
          console.warn(`检测到渲染卡顿: ${delta.toFixed(2)}ms`);
          
          // 如果卡顿率超过20%，输出优化建议
          if (totalFramesRef.current > 30 && (lagCountRef.current / totalFramesRef.current) > 0.2) {
            console.warn('渲染性能优化建议: 考虑减少同时渲染的元素数量或降低渲染频率');
          }
        }
      }
      
      frameTimeRef.current = timestamp;
      rafId = requestAnimationFrame(monitorFrameRate);
    };
    
    rafId = requestAnimationFrame(monitorFrameRate);
    
    // 每分钟重置计数器
    const intervalId = setInterval(() => {
      lagCountRef.current = 0;
      totalFramesRef.current = 0;
    }, 60000);
    
    return () => {
      cancelAnimationFrame(rafId);
      clearInterval(intervalId);
    };
  }, []);
  
  // 返回获取当前卡顿率的函数
  return () => {
    if (totalFramesRef.current === 0) return 0;
    return lagCountRef.current / totalFramesRef.current;
  };
};